import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TeacherStudentCard, { TeacherStudentAction, TeacherStudentGrid } from './TeacherStudentCard';
import { attendanceToday } from './TeacherAttendanceBoard';
import TeacherAwardFeedback from './TeacherAwardFeedback';
import './TeacherAttendanceBoard.css';
import './TeacherMaterialBoard.css';

const choices = [{ value: 'missing', label: 'Eksik' }, { value: 'complete', label: 'Tam' }];
const label = status => choices.find(item => item.value === status)?.label || 'Kayıtsız';
const collator = new Intl.Collator('tr', { sensitivity: 'base', numeric: true });
const sorted = items => [...items].sort((a, b) => collator.compare(`${a.first_name} ${a.last_name}`, `${b.first_name} ${b.last_name}`)
  || Number(a.student_id) - Number(b.student_id));

const TeacherMaterialBoard = forwardRef(function TeacherMaterialBoard({ classCode, onRequest, onBack, onLogout, onPendingChange }, ref) {
  const [day, setDay] = useState(attendanceToday);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pending, setPending] = useState({});
  const [bulkPending, setBulkPending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [pointFeedback, setPointFeedback] = useState({});
  const awardSequence = useRef(0);
  const [errors, setErrors] = useState({});
  const [drafts, setDrafts] = useState({});
  const [openNotes, setOpenNotes] = useState({});
  const [dialog, setDialog] = useState(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [dayChanged, setDayChanged] = useState(false);
  const dayRef = useRef(day);
  const generation = useRef(0);
  const alive = useRef(false);
  const locks = useRef(new Set());
  const bulkLock = useRef(false);
  const hasPending = bulkPending || Object.keys(pending).length > 0;
  const dirty = Object.entries(drafts).some(([id, text]) =>
    text !== (students.find(item => String(item.student_id) === id)?.notes || ''));

  const request = useCallback(async (path, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try { return await onRequest(path, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timer); }
  }, [onRequest]);

  const loadBoard = useCallback(async (preserveDrafts = false) => {
    const current = ++generation.current;
    setLoading(true); setLoadError('');
    try {
      const payload = await request('materials');
      if (!alive.current || current !== generation.current) return;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.date) || !Array.isArray(payload.students)) throw new Error('Unexpected material board response');
      setStudents(sorted(payload.students));
      dayRef.current = payload.date;
      setDay(payload.date);
      setDayChanged(payload.date !== attendanceToday());
      if (!preserveDrafts) { setDrafts({}); setOpenNotes({}); }
      setErrors({}); setNeedsRefresh(false); setNotice(null); setPointFeedback({});
    } catch (error) {
      if (alive.current && current === generation.current && error.status !== 401) setLoadError('Materyal kayıtları yüklenemedi. Yeniden deneyin.');
    } finally {
      if (alive.current && current === generation.current) setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    alive.current = true;
    loadBoard();
    return () => { alive.current = false; generation.current += 1; };
  }, [loadBoard]);

  useEffect(() => {
    const checkToday = () => { if (attendanceToday() !== dayRef.current) setDayChanged(true); };
    const timer = setInterval(checkToday, 30000);
    window.addEventListener('focus', checkToday);
    document.addEventListener('visibilitychange', checkToday);
    return () => { clearInterval(timer); window.removeEventListener('focus', checkToday);
      document.removeEventListener('visibilitychange', checkToday); };
  }, []);

  useEffect(() => { onPendingChange?.(hasPending); }, [hasPending, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const attemptLeave = useCallback(action => {
    if (locks.current.size || bulkLock.current || loading) return;
    if (dirty) setDialog({ title: 'Kaydedilmemiş açıklamanız var.', detail: 'Kaydetmeden devam ederseniz değişiklikler kaybolacak.', confirm: action });
    else action();
  }, [dirty, loading]);

  useImperativeHandle(ref, () => ({ requestLeave: attemptLeave }), [attemptLeave]);

  const refresh = () => {
    if (hasPending) return;
    const changed = attendanceToday() !== dayRef.current;
    // At midnight the previous day's drafts cannot be saved to today's records.
    // A same-day refresh retains local note drafts after uncertain writes.
    const action = () => loadBoard(!changed);
    if (changed) attemptLeave(action);
    else action();
  };
  const update = record => {
    if (record.date !== dayRef.current) return;
    setStudents(items => items.map(item => Number(item.student_id) === Number(record.student_id)
      ? { ...item, status: record.status, notes: record.notes || '' } : item));
  };

  const save = async (student, kind, value, actionValue = value) => {
    const id = String(student.student_id), date = dayRef.current, loadId = generation.current;
    if (attendanceToday() !== date) { setDayChanged(true); return; }
    if (dayChanged || needsRefresh || loadError || loading || locks.current.has(id) || bulkLock.current) return;
    if (kind === 'note' && !student.status) return;
    locks.current.add(id);
    setNotice(null);
    setPointFeedback(current => { const next = { ...current }; delete next[id]; return next; });
    setPending(current => ({ ...current, [id]: { kind, value: actionValue } }));
    setErrors(current => ({ ...current, [id]: null }));
    try {
      const result = await request(`materials/students/${id}${kind === 'note' ? '/note' : ''}`, {
        method: 'PATCH',
        body: JSON.stringify(kind === 'note' ? { notes: value } : { status: value }),
      });
      if (!alive.current || generation.current !== loadId) return;
      if (result.record?.date !== date || Number(result.record.student_id) !== Number(id)
        || ![null, 'complete', 'missing'].includes(result.record.status)) throw new Error('Invalid response');
      update(result.record);
      if (kind === 'status' && value === 'complete' && result.record.status === 'complete' && result.awarded_points === 2) {
        const sequence = ++awardSequence.current;
        setPointFeedback(current => ({ ...current, [id]: sequence }));
      }
      if (kind === 'status' && value === null) {
        // Clearing a material record also deletes its note in the canonical admin model.
        setDrafts(current => { const next = { ...current }; delete next[id]; return next; });
        setOpenNotes(current => ({ ...current, [id]: false }));
      }
      if (kind === 'note') setDrafts(current => {
        const next = { ...current };
        if (next[id] === value) delete next[id];
        return next;
      });
    } catch (error) {
      if (alive.current && generation.current === loadId && error.status !== 401) {
        setNeedsRefresh(true);
        setErrors(current => ({ ...current, [id]: 'Kayıt sonucu doğrulanamadı. Tekrar değiştirmeden önce verileri yenileyin.' }));
      }
    } finally {
      locks.current.delete(id);
      if (alive.current) setPending(current => {
        const next = { ...current }; delete next[id]; return next;
      });
    }
  };

  const selectStatus = (student, status) => {
    const next = student.status === status ? null : status;
    const id = String(student.student_id);
    if (next == null && (student.notes || drafts[id])) {
      setDialog({ title: 'Değerlendirme kaldırılsın mı?', detail: 'Bu öğrencinin kayıtlı materyal notu da kaldırılır.',
        confirm: () => save(student, 'status', null, status) });
    } else save(student, 'status', next, status);
  };

  const bulk = async () => {
    if (attendanceToday() !== dayRef.current) { setDayChanged(true); return; }
    if (hasPending || dayChanged || needsRefresh || loadError || loading || locks.current.size || bulkLock.current) return;
    bulkLock.current = true; setBulkPending(true); setNotice(null); setPointFeedback({});
    const loadId = generation.current;
    try {
      const result = await request('materials/mark-unrecorded-complete', { method: 'POST', body: '{}' });
      if (!alive.current || generation.current !== loadId) return;
      if (!Array.isArray(result.records) || !Number.isInteger(result.updated) ||
        result.updated !== result.records.length || result.records.some(r => r.status !== 'complete')) throw new Error('Invalid bulk response');
      const updates = new Set(result.records.map(r => String(r.student_id)));
      setStudents(items => items.map(item => updates.has(String(item.student_id)) ? { ...item, status: 'complete' } : item));
      const awards = {};
      for (const record of result.records) {
        if (record.awarded_points === 2) awards[String(record.student_id)] = ++awardSequence.current;
      }
      setPointFeedback(awards);
      setNotice({ severity: 'success', text: `${result.updated} öğrenci Tam yapıldı. ${result.skipped_absent || 0} devamsız öğrenci atlandı.` });
    } catch (error) {
      if (alive.current && generation.current === loadId && error.status !== 401) {
        setNeedsRefresh(true);
        setNotice({ severity: 'error', text: 'Toplu işlem sonucu doğrulanamadı. Yenileyerek mevcut kayıtları kontrol edin.' });
      }
    } finally {
      bulkLock.current = false;
      if (alive.current) setBulkPending(false);
    }
  };

  const counts = useMemo(() => students.reduce((acc, student) => {
    acc[student.status || 'unrecorded'] += 1; return acc;
  }, { complete: 0, missing: 0, unrecorded: 0 }), [students]);

  const dayLabel = new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Istanbul',
  }).format(new Date(`${day}T12:00:00+03:00`));

  return <Box className="ta-board tm-board">
    <header className="ta-heading">
      <Box className="ta-heading__copy">
        <Button startIcon={<ArrowBackRounded />} disabled={hasPending} onClick={() => attemptLeave(onBack)}>Öğretmen Modu</Button>
        <Typography component="h1">🎒 Öğrenci Materyalleri</Typography>
        <Typography>{classCode || 'Bu'} Sınıfı · Öğretmen Modu</Typography>
      </Box>
      <Box className="ta-heading__actions">
        <span className="ta-date" aria-label="Bugünün değerlendirme tarihi">Bugün — {dayLabel}</span>
        <Button variant="outlined" disabled={hasPending} onClick={() => attemptLeave(onLogout)}>Mini Görevlere Dön</Button>
      </Box>
    </header>
    <Box className="ta-toolbar">
      <Box className="ta-counts" aria-label="Materyal özeti">
        {[['complete', 'Tam'], ['missing', 'Eksik'], ['unrecorded', 'Kayıtsız']].map(([key, value]) =>
          <Box key={key} className={`ta-count ta-count--${key}`}><strong>{counts[key]}</strong><span>{value}</span></Box>)}
      </Box>
      <Button variant="contained" onClick={bulk}
        disabled={loading || !!loadError || hasPending || dayChanged || needsRefresh || !students.some(item => !item.status && !item.is_absent)}>
        {bulkPending ? 'Kaydediliyor…' : 'Kayıtsızları Tam Yap'}
      </Button>
    </Box>
    <Typography className="tm-bulk-hint">Toplu işlem devamsızları ve daha önce değerlendirilmiş öğrencileri değiştirmez.</Typography>
    {dayChanged && <Alert className="ta-notice" severity="warning"
      action={<Button disabled={hasPending} onClick={refresh}>Bugünü Aç</Button>}>
      Gün değişti. Materyalleri bugün için yenileyin.
    </Alert>}
    {notice && <Alert className="ta-notice" severity={notice.severity} role="status" onClose={() => setNotice(null)}>{notice.text}</Alert>}
    {needsRefresh && <Alert className="ta-notice" severity="warning" action={<Button onClick={refresh} disabled={hasPending}>Yenile</Button>}>İşleme devam etmeden önce sunucudaki durumu doğrulayın.</Alert>}
    {loadError && <Alert className="ta-notice" severity="error" action={<Button onClick={refresh}>Yeniden dene</Button>}>{loadError}</Alert>}
    {loading && !students.length && <Box className="ta-loading"><CircularProgress /><Typography>Materyaller yükleniyor…</Typography></Box>}
    {!loading && !loadError && !students.length && <Box className="ta-empty">Bu sınıfta henüz öğrenci bulunmuyor.</Box>}
    <TeacherStudentGrid label="Öğrenci materyal değerlendirmeleri" busy={loading}>
      {students.map(student => {
        const id = String(student.student_id);
        const name = `${student.first_name} ${student.last_name}`.trim();
        const disabled = (hasPending && (bulkPending || !!pending[id])) || dayChanged || loading || !!loadError || needsRefresh;
        const operation = pending[id];
        const note = drafts[id] ?? student.notes ?? '';
        const noteDirty = note !== (student.notes || '');
        return <TeacherStudentCard key={id} studentId={id} name={name}
          avatarId={student.avatar_id} starterEmoji={student.starter_emoji}
          avatarOverlay={pointFeedback[id] && <TeacherAwardFeedback key={pointFeedback[id]} label="⭐ +2" studentName={name} />}
          ariaLabel={`${name} materyal değerlendirmesi`} busy={!!pending[id] || bulkPending}
          statusLabel={label(student.status)} tone={({ complete: 'success', missing: 'warning' })[student.status] || 'neutral'}
          identityDetails={student.is_absent && <span className="tm-absent">Devamsız · elle değerlendirilebilir</span>}
          identityAction={<IconButton className="ta-note-toggle" aria-label={`${name} açıklaması`}
            aria-expanded={!!openNotes[id]} onClick={() => setOpenNotes(items => ({ ...items, [id]: !items[id] }))}>
            <Badge color="primary" variant="dot" invisible={!student.notes && !noteDirty}><EditNoteRounded /></Badge>
          </IconButton>}
          actionColumns={2} actionsLabel={`${name} materyal durumu`}
          actions={choices.map(choice => <TeacherStudentAction loading={operation?.kind === 'status' && operation.value === choice.value} className={`ta-choice ta-choice--${choice.value}`} key={choice.value}
            aria-pressed={student.status === choice.value} disabled={disabled}
            onClick={() => selectStatus(student, choice.value)}>{choice.label}</TeacherStudentAction>)}>
          {errors[id] && <Alert className="ta-card__error" severity="error">{errors[id]}</Alert>}
          {openNotes[id] && <Box className="ta-note-editor">
            <TextField fullWidth multiline minRows={2} label={`${name} açıklaması`} inputProps={{ maxLength: 500 }}
              value={note} onChange={event => setDrafts(items => ({ ...items, [id]: event.target.value }))} />
            {!student.status && <Typography className="ta-note-hint">Notu kaydetmek için önce Tam veya Eksik seçin.</Typography>}
            <Box className="ta-note-actions">
              <TeacherStudentAction fullWidth={false} loading={operation?.kind === 'note'} variant="contained" disabled={!noteDirty || !student.status || disabled}
                onClick={() => save(student, 'note', note)}>Notu Kaydet</TeacherStudentAction>
              <Button disabled={disabled} onClick={() => { setDrafts(items => {
                const next = { ...items }; delete next[id]; return next;
              }); setOpenNotes(items => ({ ...items, [id]: false })); }}>Vazgeç</Button>
            </Box>
          </Box>}
        </TeacherStudentCard>;
      })}
    </TeacherStudentGrid>
    <Button className="ta-refresh" disabled={hasPending} startIcon={<RefreshRounded />} onClick={refresh}>Materyalleri Yenile</Button>
    <Dialog open={!!dialog} onClose={() => setDialog(null)} aria-labelledby="material-dialog-title">
      <DialogTitle id="material-dialog-title">{dialog?.title}</DialogTitle><DialogContent>{dialog?.detail}</DialogContent>
      <DialogActions><Button onClick={() => setDialog(null)}>Vazgeç</Button>
        <Button color="error" onClick={() => { const action = dialog?.confirm; setDialog(null); action?.(); }}>Devam Et</Button></DialogActions>
    </Dialog>
  </Box>;
});

export default TeacherMaterialBoard;
